import * as z from 'zod';

export const CreateFulfillmentMethodRequest = z.object({
  fulfillmentMethod: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Fulfillment method ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the fulfillment method was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the fulfillment method was last updated.')
          .optional()
          .nullable(),
        type: z
          .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
          .optional(),
        name: z
          .string()
          .describe('Fulfillment method name.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        enabled: z
          .boolean()
          .describe('Whether the fulfillment method is enabled.')
          .optional()
          .nullable(),
        fee: z
          .string()
          .describe('Fee for using this fulfillment method.')
          .optional()
          .nullable(),
        availability: z
          .object({
            availableTimes: z
              .array(
                z.object({
                  dayOfWeek: z
                    .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                    .optional(),
                  timeRanges: z
                    .array(
                      z.object({
                        startTime: z
                          .object({
                            hours: z
                              .number()
                              .int()
                              .describe(
                                'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                            minutes: z
                              .number()
                              .int()
                              .describe(
                                'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The start time in time of day representation.'
                          )
                          .optional(),
                        endTime: z
                          .object({
                            hours: z
                              .number()
                              .int()
                              .describe(
                                'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                            minutes: z
                              .number()
                              .int()
                              .describe(
                                'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The end time in time of day representation.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'The timezone in which the availability times are given.'
              )
              .optional()
              .nullable(),
          })
          .describe('Availability of this fulfillment method.')
          .optional(),
        minOrderPrice: z
          .string()
          .describe(
            'Minimum order price to qualify for using this fulfillment method.'
          )
          .optional()
          .nullable(),
        businessLocationId: z
          .string()
          .describe(
            'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags used to classify and sort different types of fulfillment methods.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          deliveryOptions: z.never().optional(),
          pickupOptions: z
            .object({
              instructions: z
                .string()
                .describe('Instructions for the pickup.')
                .max(250)
                .optional()
                .nullable(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe(
                  'Pickup address.\n\nThis is set to the address of the restaurant.'
                )
                .optional(),
            })
            .describe('Data specific for pickup fulfillment method.'),
        }),
        z.object({
          pickupOptions: z.never().optional(),
          deliveryOptions: z
            .object({
              deliveryTimeInMinutes: z
                .number()
                .int()
                .describe('Estimated delivery time in minutes.')
                .min(0)
                .optional()
                .nullable(),
              freeDeliveryThreshold: z
                .string()
                .describe(
                  'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                )
                .optional()
                .nullable(),
              deliveryArea: z
                .intersection(
                  z.object({
                    type: z
                      .enum([
                        'UNKNOWN_DELIVERY_AREA',
                        'RADIUS',
                        'POSTAL_CODE',
                        'CUSTOM',
                        'PROVIDER_DEFINED',
                      ])
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      radiusOptions: z.never().optional(),
                      postalCodeOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                    }),
                    z.object({
                      postalCodeOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                      radiusOptions: z
                        .object({
                          minDistance: z
                            .string()
                            .describe(
                              'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                            )
                            .optional()
                            .nullable(),
                          maxDistance: z
                            .string()
                            .describe(
                              'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                            )
                            .optional()
                            .nullable(),
                          centerPointAddress: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                              addressLine2: z.string().optional().nullable(),
                            })
                            .describe('Address at the center of the circle.')
                            .optional(),
                          unit: z
                            .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                            .describe('Unit of measurement of the radius.')
                            .optional(),
                        })
                        .describe('Settings for a radius delivery area.'),
                    }),
                    z.object({
                      radiusOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                      postalCodeOptions: z
                        .object({
                          countryCode: z
                            .string()
                            .describe(
                              'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                            )
                            .optional()
                            .nullable(),
                          postalCodes: z.array(z.string()).max(100).optional(),
                        })
                        .describe('Settings for a postal code delivery area.'),
                    }),
                    z.object({
                      radiusOptions: z.never().optional(),
                      postalCodeOptions: z.never().optional(),
                      customOptions: z
                        .object({
                          geocodes: z
                            .array(
                              z.object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                            )
                            .optional(),
                        })
                        .describe('Settings for a custom delivery area.'),
                    }),
                  ])
                )
                .describe(
                  'Delivery area supported by this delivery fulfillment method.'
                )
                .optional(),
              deliveryProviderAppId: z
                .string()
                .describe('Delivery provider app id.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              courierPickupInstructions: z
                .string()
                .describe('Pickup instructions for couriers.')
                .max(250)
                .optional()
                .nullable(),
            })
            .describe('Data specific for delivery fulfillment method.'),
        }),
      ])
    )
    .describe('Fulfillment method to create.'),
});
export const CreateFulfillmentMethodResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Fulfillment method ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the fulfillment method was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the fulfillment method was last updated.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
      .describe('Type of fulfillment method.')
      .optional(),
    name: z
      .string()
      .describe('Fulfillment method name.')
      .min(1)
      .max(30)
      .optional()
      .nullable(),
    enabled: z
      .boolean()
      .describe('Whether the fulfillment method is enabled.')
      .optional()
      .nullable(),
    fee: z
      .string()
      .describe('Fee for using this fulfillment method.')
      .optional()
      .nullable(),
    availability: z
      .object({
        availableTimes: z
          .array(
            z.object({
              dayOfWeek: z
                .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                .describe('The day of week this availability relates to.')
                .optional(),
              timeRanges: z
                .array(
                  z.object({
                    startTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The start time in time of day representation.')
                      .optional(),
                    endTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The end time in time of day representation.')
                      .optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
        timeZone: z
          .string()
          .describe('The timezone in which the availability times are given.')
          .optional()
          .nullable(),
      })
      .describe('Availability of this fulfillment method.')
      .optional(),
    minOrderPrice: z
      .string()
      .describe(
        'Minimum order price to qualify for using this fulfillment method.'
      )
      .optional()
      .nullable(),
    businessLocationId: z
      .string()
      .describe(
        'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        'Tags used to classify and sort different types of fulfillment methods.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z.never().optional(),
    }),
    z.object({
      deliveryOptions: z.never().optional(),
      pickupOptions: z
        .object({
          instructions: z
            .string()
            .describe('Instructions for the pickup.')
            .max(250)
            .optional()
            .nullable(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe(
              'Pickup address.\n\nThis is set to the address of the restaurant.'
            )
            .optional(),
        })
        .describe('Data specific for pickup fulfillment method.'),
    }),
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z
        .object({
          deliveryTimeInMinutes: z
            .number()
            .int()
            .describe('Estimated delivery time in minutes.')
            .min(0)
            .optional()
            .nullable(),
          freeDeliveryThreshold: z
            .string()
            .describe(
              'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
            )
            .optional()
            .nullable(),
          deliveryArea: z
            .intersection(
              z.object({
                type: z
                  .enum([
                    'UNKNOWN_DELIVERY_AREA',
                    'RADIUS',
                    'POSTAL_CODE',
                    'CUSTOM',
                    'PROVIDER_DEFINED',
                  ])
                  .describe('Type of delivery area.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                }),
                z.object({
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  radiusOptions: z
                    .object({
                      minDistance: z
                        .string()
                        .describe(
                          'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      maxDistance: z
                        .string()
                        .describe(
                          'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      centerPointAddress: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address at the center of the circle.')
                        .optional(),
                      unit: z
                        .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                        .describe('Unit of measurement of the radius.')
                        .optional(),
                    })
                    .describe('Settings for a radius delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  postalCodeOptions: z
                    .object({
                      countryCode: z
                        .string()
                        .describe(
                          'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                        )
                        .optional()
                        .nullable(),
                      postalCodes: z.array(z.string()).max(100).optional(),
                    })
                    .describe('Settings for a postal code delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z
                    .object({
                      geocodes: z
                        .array(
                          z.object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe('Settings for a custom delivery area.'),
                }),
              ])
            )
            .describe(
              'Delivery area supported by this delivery fulfillment method.'
            )
            .optional(),
          deliveryProviderAppId: z
            .string()
            .describe('Delivery provider app id.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          courierPickupInstructions: z
            .string()
            .describe('Pickup instructions for couriers.')
            .max(250)
            .optional()
            .nullable(),
        })
        .describe('Data specific for delivery fulfillment method.'),
    }),
  ])
);
export const BulkCreateFulfillmentMethodsRequest = z.object({
  options: z
    .object({
      fulfillmentMethods: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Fulfillment method ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the fulfillment method was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the fulfillment method was last updated.'
                )
                .optional()
                .nullable(),
              type: z
                .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
                .optional(),
              name: z
                .string()
                .describe('Fulfillment method name.')
                .min(1)
                .max(30)
                .optional()
                .nullable(),
              enabled: z
                .boolean()
                .describe('Whether the fulfillment method is enabled.')
                .optional()
                .nullable(),
              fee: z
                .string()
                .describe('Fee for using this fulfillment method.')
                .optional()
                .nullable(),
              availability: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  timeZone: z
                    .string()
                    .describe(
                      'The timezone in which the availability times are given.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Availability of this fulfillment method.')
                .optional(),
              minOrderPrice: z
                .string()
                .describe(
                  'Minimum order price to qualify for using this fulfillment method.'
                )
                .optional()
                .nullable(),
              businessLocationId: z
                .string()
                .describe(
                  'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Extended fields.')
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  'Tags used to classify and sort different types of fulfillment methods.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                pickupOptions: z.never().optional(),
                deliveryOptions: z.never().optional(),
              }),
              z.object({
                deliveryOptions: z.never().optional(),
                pickupOptions: z
                  .object({
                    instructions: z
                      .string()
                      .describe('Instructions for the pickup.')
                      .max(250)
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe(
                        'Pickup address.\n\nThis is set to the address of the restaurant.'
                      )
                      .optional(),
                  })
                  .describe('Data specific for pickup fulfillment method.'),
              }),
              z.object({
                pickupOptions: z.never().optional(),
                deliveryOptions: z
                  .object({
                    deliveryTimeInMinutes: z
                      .number()
                      .int()
                      .describe('Estimated delivery time in minutes.')
                      .min(0)
                      .optional()
                      .nullable(),
                    freeDeliveryThreshold: z
                      .string()
                      .describe(
                        'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                      )
                      .optional()
                      .nullable(),
                    deliveryArea: z
                      .intersection(
                        z.object({
                          type: z
                            .enum([
                              'UNKNOWN_DELIVERY_AREA',
                              'RADIUS',
                              'POSTAL_CODE',
                              'CUSTOM',
                              'PROVIDER_DEFINED',
                            ])
                            .describe('Type of delivery area.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            radiusOptions: z.never().optional(),
                            postalCodeOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                          }),
                          z.object({
                            postalCodeOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                            radiusOptions: z
                              .object({
                                minDistance: z
                                  .string()
                                  .describe(
                                    'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                                  )
                                  .optional()
                                  .nullable(),
                                maxDistance: z
                                  .string()
                                  .describe(
                                    'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                                  )
                                  .optional()
                                  .nullable(),
                                centerPointAddress: z
                                  .object({
                                    city: z.string().optional().nullable(),
                                    subdivision: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    country: z.string().optional().nullable(),
                                    postalCode: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    addressLine1: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    addressLine2: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Address at the center of the circle.'
                                  )
                                  .optional(),
                                unit: z
                                  .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                                  .describe(
                                    'Unit of measurement of the radius.'
                                  )
                                  .optional(),
                              })
                              .describe('Settings for a radius delivery area.'),
                          }),
                          z.object({
                            radiusOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                            postalCodeOptions: z
                              .object({
                                countryCode: z
                                  .string()
                                  .describe(
                                    'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                                  )
                                  .optional()
                                  .nullable(),
                                postalCodes: z
                                  .array(z.string())
                                  .max(100)
                                  .optional(),
                              })
                              .describe(
                                'Settings for a postal code delivery area.'
                              ),
                          }),
                          z.object({
                            radiusOptions: z.never().optional(),
                            postalCodeOptions: z.never().optional(),
                            customOptions: z
                              .object({
                                geocodes: z
                                  .array(
                                    z.object({
                                      latitude: z
                                        .number()
                                        .describe('Address latitude.')
                                        .optional()
                                        .nullable(),
                                      longitude: z
                                        .number()
                                        .describe('Address longitude.')
                                        .optional()
                                        .nullable(),
                                    })
                                  )
                                  .optional(),
                              })
                              .describe('Settings for a custom delivery area.'),
                          }),
                        ])
                      )
                      .describe(
                        'Delivery area supported by this delivery fulfillment method.'
                      )
                      .optional(),
                    deliveryProviderAppId: z
                      .string()
                      .describe('Delivery provider app id.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    courierPickupInstructions: z
                      .string()
                      .describe('Pickup instructions for couriers.')
                      .max(250)
                      .optional()
                      .nullable(),
                  })
                  .describe('Data specific for delivery fulfillment method.'),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      returnEntity: z
        .boolean()
        .describe('If true, the created entities will be returned.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateFulfillmentMethodsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for fulfillment method creation.')
          .optional(),
        fulfillmentMethod: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Fulfillment method ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the fulfillment method was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the fulfillment method was last updated.'
                )
                .optional()
                .nullable(),
              type: z
                .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
                .describe('Type of fulfillment method.')
                .optional(),
              name: z
                .string()
                .describe('Fulfillment method name.')
                .min(1)
                .max(30)
                .optional()
                .nullable(),
              enabled: z
                .boolean()
                .describe('Whether the fulfillment method is enabled.')
                .optional()
                .nullable(),
              fee: z
                .string()
                .describe('Fee for using this fulfillment method.')
                .optional()
                .nullable(),
              availability: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .describe(
                            'The day of week this availability relates to.'
                          )
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  timeZone: z
                    .string()
                    .describe(
                      'The timezone in which the availability times are given.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Availability of this fulfillment method.')
                .optional(),
              minOrderPrice: z
                .string()
                .describe(
                  'Minimum order price to qualify for using this fulfillment method.'
                )
                .optional()
                .nullable(),
              businessLocationId: z
                .string()
                .describe(
                  'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Extended fields.')
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  'Tags used to classify and sort different types of fulfillment methods.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                pickupOptions: z.never().optional(),
                deliveryOptions: z.never().optional(),
              }),
              z.object({
                deliveryOptions: z.never().optional(),
                pickupOptions: z
                  .object({
                    instructions: z
                      .string()
                      .describe('Instructions for the pickup.')
                      .max(250)
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe(
                        'Pickup address.\n\nThis is set to the address of the restaurant.'
                      )
                      .optional(),
                  })
                  .describe('Data specific for pickup fulfillment method.'),
              }),
              z.object({
                pickupOptions: z.never().optional(),
                deliveryOptions: z
                  .object({
                    deliveryTimeInMinutes: z
                      .number()
                      .int()
                      .describe('Estimated delivery time in minutes.')
                      .min(0)
                      .optional()
                      .nullable(),
                    freeDeliveryThreshold: z
                      .string()
                      .describe(
                        'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                      )
                      .optional()
                      .nullable(),
                    deliveryArea: z
                      .intersection(
                        z.object({
                          type: z
                            .enum([
                              'UNKNOWN_DELIVERY_AREA',
                              'RADIUS',
                              'POSTAL_CODE',
                              'CUSTOM',
                              'PROVIDER_DEFINED',
                            ])
                            .describe('Type of delivery area.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            radiusOptions: z.never().optional(),
                            postalCodeOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                          }),
                          z.object({
                            postalCodeOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                            radiusOptions: z
                              .object({
                                minDistance: z
                                  .string()
                                  .describe(
                                    'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                                  )
                                  .optional()
                                  .nullable(),
                                maxDistance: z
                                  .string()
                                  .describe(
                                    'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                                  )
                                  .optional()
                                  .nullable(),
                                centerPointAddress: z
                                  .object({
                                    city: z.string().optional().nullable(),
                                    subdivision: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    country: z.string().optional().nullable(),
                                    postalCode: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    addressLine1: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                    addressLine2: z
                                      .string()
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Address at the center of the circle.'
                                  )
                                  .optional(),
                                unit: z
                                  .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                                  .describe(
                                    'Unit of measurement of the radius.'
                                  )
                                  .optional(),
                              })
                              .describe('Settings for a radius delivery area.'),
                          }),
                          z.object({
                            radiusOptions: z.never().optional(),
                            customOptions: z.never().optional(),
                            postalCodeOptions: z
                              .object({
                                countryCode: z
                                  .string()
                                  .describe(
                                    'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                                  )
                                  .optional()
                                  .nullable(),
                                postalCodes: z
                                  .array(z.string())
                                  .max(100)
                                  .optional(),
                              })
                              .describe(
                                'Settings for a postal code delivery area.'
                              ),
                          }),
                          z.object({
                            radiusOptions: z.never().optional(),
                            postalCodeOptions: z.never().optional(),
                            customOptions: z
                              .object({
                                geocodes: z
                                  .array(
                                    z.object({
                                      latitude: z
                                        .number()
                                        .describe('Address latitude.')
                                        .optional()
                                        .nullable(),
                                      longitude: z
                                        .number()
                                        .describe('Address longitude.')
                                        .optional()
                                        .nullable(),
                                    })
                                  )
                                  .optional(),
                              })
                              .describe('Settings for a custom delivery area.'),
                          }),
                        ])
                      )
                      .describe(
                        'Delivery area supported by this delivery fulfillment method.'
                      )
                      .optional(),
                    deliveryProviderAppId: z
                      .string()
                      .describe('Delivery provider app id.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    courierPickupInstructions: z
                      .string()
                      .describe('Pickup instructions for couriers.')
                      .max(250)
                      .optional()
                      .nullable(),
                  })
                  .describe('Data specific for delivery fulfillment method.'),
              }),
            ])
          )
          .describe('Created fulfillment method.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const GetFulfillmentMethodRequest = z.object({
  fulfillmentMethodId: z
    .string()
    .describe('The ID of the fulfillment method to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetFulfillmentMethodResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Fulfillment method ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the fulfillment method was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the fulfillment method was last updated.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
      .describe('Type of fulfillment method.')
      .optional(),
    name: z
      .string()
      .describe('Fulfillment method name.')
      .min(1)
      .max(30)
      .optional()
      .nullable(),
    enabled: z
      .boolean()
      .describe('Whether the fulfillment method is enabled.')
      .optional()
      .nullable(),
    fee: z
      .string()
      .describe('Fee for using this fulfillment method.')
      .optional()
      .nullable(),
    availability: z
      .object({
        availableTimes: z
          .array(
            z.object({
              dayOfWeek: z
                .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                .describe('The day of week this availability relates to.')
                .optional(),
              timeRanges: z
                .array(
                  z.object({
                    startTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The start time in time of day representation.')
                      .optional(),
                    endTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The end time in time of day representation.')
                      .optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
        timeZone: z
          .string()
          .describe('The timezone in which the availability times are given.')
          .optional()
          .nullable(),
      })
      .describe('Availability of this fulfillment method.')
      .optional(),
    minOrderPrice: z
      .string()
      .describe(
        'Minimum order price to qualify for using this fulfillment method.'
      )
      .optional()
      .nullable(),
    businessLocationId: z
      .string()
      .describe(
        'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        'Tags used to classify and sort different types of fulfillment methods.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z.never().optional(),
    }),
    z.object({
      deliveryOptions: z.never().optional(),
      pickupOptions: z
        .object({
          instructions: z
            .string()
            .describe('Instructions for the pickup.')
            .max(250)
            .optional()
            .nullable(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe(
              'Pickup address.\n\nThis is set to the address of the restaurant.'
            )
            .optional(),
        })
        .describe('Data specific for pickup fulfillment method.'),
    }),
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z
        .object({
          deliveryTimeInMinutes: z
            .number()
            .int()
            .describe('Estimated delivery time in minutes.')
            .min(0)
            .optional()
            .nullable(),
          freeDeliveryThreshold: z
            .string()
            .describe(
              'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
            )
            .optional()
            .nullable(),
          deliveryArea: z
            .intersection(
              z.object({
                type: z
                  .enum([
                    'UNKNOWN_DELIVERY_AREA',
                    'RADIUS',
                    'POSTAL_CODE',
                    'CUSTOM',
                    'PROVIDER_DEFINED',
                  ])
                  .describe('Type of delivery area.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                }),
                z.object({
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  radiusOptions: z
                    .object({
                      minDistance: z
                        .string()
                        .describe(
                          'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      maxDistance: z
                        .string()
                        .describe(
                          'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      centerPointAddress: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address at the center of the circle.')
                        .optional(),
                      unit: z
                        .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                        .describe('Unit of measurement of the radius.')
                        .optional(),
                    })
                    .describe('Settings for a radius delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  postalCodeOptions: z
                    .object({
                      countryCode: z
                        .string()
                        .describe(
                          'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                        )
                        .optional()
                        .nullable(),
                      postalCodes: z.array(z.string()).max(100).optional(),
                    })
                    .describe('Settings for a postal code delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z
                    .object({
                      geocodes: z
                        .array(
                          z.object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe('Settings for a custom delivery area.'),
                }),
              ])
            )
            .describe(
              'Delivery area supported by this delivery fulfillment method.'
            )
            .optional(),
          deliveryProviderAppId: z
            .string()
            .describe('Delivery provider app id.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          courierPickupInstructions: z
            .string()
            .describe('Pickup instructions for couriers.')
            .max(250)
            .optional()
            .nullable(),
        })
        .describe('Data specific for delivery fulfillment method.'),
    }),
  ])
);
export const UpdateFulfillmentMethodRequest = z.object({
  _id: z
    .string()
    .describe('Fulfillment method ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  fulfillmentMethod: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Fulfillment method ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the fulfillment method was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the fulfillment method was last updated.')
          .optional()
          .nullable(),
        type: z
          .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
          .optional(),
        name: z
          .string()
          .describe('Fulfillment method name.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        enabled: z
          .boolean()
          .describe('Whether the fulfillment method is enabled.')
          .optional()
          .nullable(),
        fee: z
          .string()
          .describe('Fee for using this fulfillment method.')
          .optional()
          .nullable(),
        availability: z
          .object({
            availableTimes: z
              .array(
                z.object({
                  dayOfWeek: z
                    .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                    .optional(),
                  timeRanges: z
                    .array(
                      z.object({
                        startTime: z
                          .object({
                            hours: z
                              .number()
                              .int()
                              .describe(
                                'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                            minutes: z
                              .number()
                              .int()
                              .describe(
                                'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The start time in time of day representation.'
                          )
                          .optional(),
                        endTime: z
                          .object({
                            hours: z
                              .number()
                              .int()
                              .describe(
                                'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                            minutes: z
                              .number()
                              .int()
                              .describe(
                                'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The end time in time of day representation.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'The timezone in which the availability times are given.'
              )
              .optional()
              .nullable(),
          })
          .describe('Availability of this fulfillment method.')
          .optional(),
        minOrderPrice: z
          .string()
          .describe(
            'Minimum order price to qualify for using this fulfillment method.'
          )
          .optional()
          .nullable(),
        businessLocationId: z
          .string()
          .describe(
            'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags used to classify and sort different types of fulfillment methods.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          pickupOptions: z.never().optional(),
          deliveryOptions: z.never().optional(),
        }),
        z.object({
          deliveryOptions: z.never().optional(),
          pickupOptions: z
            .object({
              instructions: z
                .string()
                .describe('Instructions for the pickup.')
                .max(250)
                .optional()
                .nullable(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe(
                  'Pickup address.\n\nThis is set to the address of the restaurant.'
                )
                .optional(),
            })
            .describe('Data specific for pickup fulfillment method.'),
        }),
        z.object({
          pickupOptions: z.never().optional(),
          deliveryOptions: z
            .object({
              deliveryTimeInMinutes: z
                .number()
                .int()
                .describe('Estimated delivery time in minutes.')
                .min(0)
                .optional()
                .nullable(),
              freeDeliveryThreshold: z
                .string()
                .describe(
                  'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                )
                .optional()
                .nullable(),
              deliveryArea: z
                .intersection(
                  z.object({
                    type: z
                      .enum([
                        'UNKNOWN_DELIVERY_AREA',
                        'RADIUS',
                        'POSTAL_CODE',
                        'CUSTOM',
                        'PROVIDER_DEFINED',
                      ])
                      .describe('Type of delivery area.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      radiusOptions: z.never().optional(),
                      postalCodeOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                    }),
                    z.object({
                      postalCodeOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                      radiusOptions: z
                        .object({
                          minDistance: z
                            .string()
                            .describe(
                              'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                            )
                            .optional()
                            .nullable(),
                          maxDistance: z
                            .string()
                            .describe(
                              'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                            )
                            .optional()
                            .nullable(),
                          centerPointAddress: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                              addressLine2: z.string().optional().nullable(),
                            })
                            .describe('Address at the center of the circle.')
                            .optional(),
                          unit: z
                            .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                            .describe('Unit of measurement of the radius.')
                            .optional(),
                        })
                        .describe('Settings for a radius delivery area.'),
                    }),
                    z.object({
                      radiusOptions: z.never().optional(),
                      customOptions: z.never().optional(),
                      postalCodeOptions: z
                        .object({
                          countryCode: z
                            .string()
                            .describe(
                              'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                            )
                            .optional()
                            .nullable(),
                          postalCodes: z.array(z.string()).max(100).optional(),
                        })
                        .describe('Settings for a postal code delivery area.'),
                    }),
                    z.object({
                      radiusOptions: z.never().optional(),
                      postalCodeOptions: z.never().optional(),
                      customOptions: z
                        .object({
                          geocodes: z
                            .array(
                              z.object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                            )
                            .optional(),
                        })
                        .describe('Settings for a custom delivery area.'),
                    }),
                  ])
                )
                .describe(
                  'Delivery area supported by this delivery fulfillment method.'
                )
                .optional(),
              deliveryProviderAppId: z
                .string()
                .describe('Delivery provider app id.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              courierPickupInstructions: z
                .string()
                .describe('Pickup instructions for couriers.')
                .max(250)
                .optional()
                .nullable(),
            })
            .describe('Data specific for delivery fulfillment method.'),
        }),
      ])
    )
    .describe('Fulfillment method information to update.'),
});
export const UpdateFulfillmentMethodResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Fulfillment method ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the fulfillment method was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the fulfillment method was last updated.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
      .describe('Type of fulfillment method.')
      .optional(),
    name: z
      .string()
      .describe('Fulfillment method name.')
      .min(1)
      .max(30)
      .optional()
      .nullable(),
    enabled: z
      .boolean()
      .describe('Whether the fulfillment method is enabled.')
      .optional()
      .nullable(),
    fee: z
      .string()
      .describe('Fee for using this fulfillment method.')
      .optional()
      .nullable(),
    availability: z
      .object({
        availableTimes: z
          .array(
            z.object({
              dayOfWeek: z
                .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                .describe('The day of week this availability relates to.')
                .optional(),
              timeRanges: z
                .array(
                  z.object({
                    startTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The start time in time of day representation.')
                      .optional(),
                    endTime: z
                      .object({
                        hours: z
                          .number()
                          .int()
                          .describe(
                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                        minutes: z
                          .number()
                          .int()
                          .describe(
                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                          )
                          .optional(),
                      })
                      .describe('The end time in time of day representation.')
                      .optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
        timeZone: z
          .string()
          .describe('The timezone in which the availability times are given.')
          .optional()
          .nullable(),
      })
      .describe('Availability of this fulfillment method.')
      .optional(),
    minOrderPrice: z
      .string()
      .describe(
        'Minimum order price to qualify for using this fulfillment method.'
      )
      .optional()
      .nullable(),
    businessLocationId: z
      .string()
      .describe(
        'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        'Tags used to classify and sort different types of fulfillment methods.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z.never().optional(),
    }),
    z.object({
      deliveryOptions: z.never().optional(),
      pickupOptions: z
        .object({
          instructions: z
            .string()
            .describe('Instructions for the pickup.')
            .max(250)
            .optional()
            .nullable(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe(
              'Pickup address.\n\nThis is set to the address of the restaurant.'
            )
            .optional(),
        })
        .describe('Data specific for pickup fulfillment method.'),
    }),
    z.object({
      pickupOptions: z.never().optional(),
      deliveryOptions: z
        .object({
          deliveryTimeInMinutes: z
            .number()
            .int()
            .describe('Estimated delivery time in minutes.')
            .min(0)
            .optional()
            .nullable(),
          freeDeliveryThreshold: z
            .string()
            .describe(
              'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
            )
            .optional()
            .nullable(),
          deliveryArea: z
            .intersection(
              z.object({
                type: z
                  .enum([
                    'UNKNOWN_DELIVERY_AREA',
                    'RADIUS',
                    'POSTAL_CODE',
                    'CUSTOM',
                    'PROVIDER_DEFINED',
                  ])
                  .describe('Type of delivery area.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                }),
                z.object({
                  postalCodeOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  radiusOptions: z
                    .object({
                      minDistance: z
                        .string()
                        .describe(
                          'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      maxDistance: z
                        .string()
                        .describe(
                          'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                        )
                        .optional()
                        .nullable(),
                      centerPointAddress: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address at the center of the circle.')
                        .optional(),
                      unit: z
                        .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                        .describe('Unit of measurement of the radius.')
                        .optional(),
                    })
                    .describe('Settings for a radius delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  customOptions: z.never().optional(),
                  postalCodeOptions: z
                    .object({
                      countryCode: z
                        .string()
                        .describe(
                          'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                        )
                        .optional()
                        .nullable(),
                      postalCodes: z.array(z.string()).max(100).optional(),
                    })
                    .describe('Settings for a postal code delivery area.'),
                }),
                z.object({
                  radiusOptions: z.never().optional(),
                  postalCodeOptions: z.never().optional(),
                  customOptions: z
                    .object({
                      geocodes: z
                        .array(
                          z.object({
                            latitude: z
                              .number()
                              .describe('Address latitude.')
                              .optional()
                              .nullable(),
                            longitude: z
                              .number()
                              .describe('Address longitude.')
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe('Settings for a custom delivery area.'),
                }),
              ])
            )
            .describe(
              'Delivery area supported by this delivery fulfillment method.'
            )
            .optional(),
          deliveryProviderAppId: z
            .string()
            .describe('Delivery provider app id.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          courierPickupInstructions: z
            .string()
            .describe('Pickup instructions for couriers.')
            .max(250)
            .optional()
            .nullable(),
        })
        .describe('Data specific for delivery fulfillment method.'),
    }),
  ])
);
export const DeleteFulfillmentMethodRequest = z.object({
  fulfillmentMethodId: z
    .string()
    .describe('The ID of the fulfillment method to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteFulfillmentMethodResponse = z.object({});
export const QueryFulfillmentMethodsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          enabled: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          fee: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          minOrderPrice: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          type: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                '_createdDate',
                '_updatedDate',
                'name',
                'enabled',
                'fee',
                'minOrderPrice',
                'type',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('The query by which to select fulfillment methods.'),
});
export const QueryFulfillmentMethodsResponse = z.object({
  fulfillmentMethods: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Fulfillment method ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the fulfillment method was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the fulfillment method was last updated.')
            .optional()
            .nullable(),
          type: z
            .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
            .describe('Type of fulfillment method.')
            .optional(),
          name: z
            .string()
            .describe('Fulfillment method name.')
            .min(1)
            .max(30)
            .optional()
            .nullable(),
          enabled: z
            .boolean()
            .describe('Whether the fulfillment method is enabled.')
            .optional()
            .nullable(),
          fee: z
            .string()
            .describe('Fee for using this fulfillment method.')
            .optional()
            .nullable(),
          availability: z
            .object({
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .optional(),
              timeZone: z
                .string()
                .describe(
                  'The timezone in which the availability times are given.'
                )
                .optional()
                .nullable(),
            })
            .describe('Availability of this fulfillment method.')
            .optional(),
          minOrderPrice: z
            .string()
            .describe(
              'Minimum order price to qualify for using this fulfillment method.'
            )
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags used to classify and sort different types of fulfillment methods.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z.never().optional(),
          }),
          z.object({
            deliveryOptions: z.never().optional(),
            pickupOptions: z
              .object({
                instructions: z
                  .string()
                  .describe('Instructions for the pickup.')
                  .max(250)
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe(
                    'Pickup address.\n\nThis is set to the address of the restaurant.'
                  )
                  .optional(),
              })
              .describe('Data specific for pickup fulfillment method.'),
          }),
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z
              .object({
                deliveryTimeInMinutes: z
                  .number()
                  .int()
                  .describe('Estimated delivery time in minutes.')
                  .min(0)
                  .optional()
                  .nullable(),
                freeDeliveryThreshold: z
                  .string()
                  .describe(
                    'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                  )
                  .optional()
                  .nullable(),
                deliveryArea: z
                  .intersection(
                    z.object({
                      type: z
                        .enum([
                          'UNKNOWN_DELIVERY_AREA',
                          'RADIUS',
                          'POSTAL_CODE',
                          'CUSTOM',
                          'PROVIDER_DEFINED',
                        ])
                        .describe('Type of delivery area.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                      }),
                      z.object({
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        radiusOptions: z
                          .object({
                            minDistance: z
                              .string()
                              .describe(
                                'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            maxDistance: z
                              .string()
                              .describe(
                                'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            centerPointAddress: z
                              .object({
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine1: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Address at the center of the circle.')
                              .optional(),
                            unit: z
                              .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                              .describe('Unit of measurement of the radius.')
                              .optional(),
                          })
                          .describe('Settings for a radius delivery area.'),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        postalCodeOptions: z
                          .object({
                            countryCode: z
                              .string()
                              .describe(
                                'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                              )
                              .optional()
                              .nullable(),
                            postalCodes: z
                              .array(z.string())
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Settings for a postal code delivery area.'
                          ),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z
                          .object({
                            geocodes: z
                              .array(
                                z.object({
                                  latitude: z
                                    .number()
                                    .describe('Address latitude.')
                                    .optional()
                                    .nullable(),
                                  longitude: z
                                    .number()
                                    .describe('Address longitude.')
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .optional(),
                          })
                          .describe('Settings for a custom delivery area.'),
                      }),
                    ])
                  )
                  .describe(
                    'Delivery area supported by this delivery fulfillment method.'
                  )
                  .optional(),
                deliveryProviderAppId: z
                  .string()
                  .describe('Delivery provider app id.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                courierPickupInstructions: z
                  .string()
                  .describe('Pickup instructions for couriers.')
                  .max(250)
                  .optional()
                  .nullable(),
              })
              .describe('Data specific for delivery fulfillment method.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const ListFulfillmentMethodsRequest = z.object({
  options: z
    .object({
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging')
        .optional(),
    })
    .describe('Options for listing the fulfillment methods.')
    .optional(),
});
export const ListFulfillmentMethodsResponse = z.object({
  fulfillmentMethods: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Fulfillment method ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the fulfillment method was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the fulfillment method was last updated.')
            .optional()
            .nullable(),
          type: z
            .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
            .describe('Type of fulfillment method.')
            .optional(),
          name: z
            .string()
            .describe('Fulfillment method name.')
            .min(1)
            .max(30)
            .optional()
            .nullable(),
          enabled: z
            .boolean()
            .describe('Whether the fulfillment method is enabled.')
            .optional()
            .nullable(),
          fee: z
            .string()
            .describe('Fee for using this fulfillment method.')
            .optional()
            .nullable(),
          availability: z
            .object({
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .optional(),
              timeZone: z
                .string()
                .describe(
                  'The timezone in which the availability times are given.'
                )
                .optional()
                .nullable(),
            })
            .describe('Availability of this fulfillment method.')
            .optional(),
          minOrderPrice: z
            .string()
            .describe(
              'Minimum order price to qualify for using this fulfillment method.'
            )
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags used to classify and sort different types of fulfillment methods.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z.never().optional(),
          }),
          z.object({
            deliveryOptions: z.never().optional(),
            pickupOptions: z
              .object({
                instructions: z
                  .string()
                  .describe('Instructions for the pickup.')
                  .max(250)
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe(
                    'Pickup address.\n\nThis is set to the address of the restaurant.'
                  )
                  .optional(),
              })
              .describe('Data specific for pickup fulfillment method.'),
          }),
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z
              .object({
                deliveryTimeInMinutes: z
                  .number()
                  .int()
                  .describe('Estimated delivery time in minutes.')
                  .min(0)
                  .optional()
                  .nullable(),
                freeDeliveryThreshold: z
                  .string()
                  .describe(
                    'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                  )
                  .optional()
                  .nullable(),
                deliveryArea: z
                  .intersection(
                    z.object({
                      type: z
                        .enum([
                          'UNKNOWN_DELIVERY_AREA',
                          'RADIUS',
                          'POSTAL_CODE',
                          'CUSTOM',
                          'PROVIDER_DEFINED',
                        ])
                        .describe('Type of delivery area.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                      }),
                      z.object({
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        radiusOptions: z
                          .object({
                            minDistance: z
                              .string()
                              .describe(
                                'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            maxDistance: z
                              .string()
                              .describe(
                                'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            centerPointAddress: z
                              .object({
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine1: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Address at the center of the circle.')
                              .optional(),
                            unit: z
                              .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                              .describe('Unit of measurement of the radius.')
                              .optional(),
                          })
                          .describe('Settings for a radius delivery area.'),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        postalCodeOptions: z
                          .object({
                            countryCode: z
                              .string()
                              .describe(
                                'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                              )
                              .optional()
                              .nullable(),
                            postalCodes: z
                              .array(z.string())
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Settings for a postal code delivery area.'
                          ),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z
                          .object({
                            geocodes: z
                              .array(
                                z.object({
                                  latitude: z
                                    .number()
                                    .describe('Address latitude.')
                                    .optional()
                                    .nullable(),
                                  longitude: z
                                    .number()
                                    .describe('Address longitude.')
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .optional(),
                          })
                          .describe('Settings for a custom delivery area.'),
                      }),
                    ])
                  )
                  .describe(
                    'Delivery area supported by this delivery fulfillment method.'
                  )
                  .optional(),
                deliveryProviderAppId: z
                  .string()
                  .describe('Delivery provider app id.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                courierPickupInstructions: z
                  .string()
                  .describe('Pickup instructions for couriers.')
                  .max(250)
                  .optional()
                  .nullable(),
              })
              .describe('Data specific for delivery fulfillment method.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const ListAvailableFulfillmentMethodsForAddressRequest = z.object({
  options: z
    .object({
      address: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'The address by which to filter delivery fulfillment methods.'
        )
        .optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging')
        .optional(),
      fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
    })
    .describe('Options for listing the available fulfillment methods.')
    .optional(),
});
export const ListAvailableFulfillmentMethodsForAddressResponse = z.object({
  fulfillmentMethods: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Fulfillment method ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the fulfillment method was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the fulfillment method was last updated.')
            .optional()
            .nullable(),
          type: z
            .enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY'])
            .describe('Type of fulfillment method.')
            .optional(),
          name: z
            .string()
            .describe('Fulfillment method name.')
            .min(1)
            .max(30)
            .optional()
            .nullable(),
          enabled: z
            .boolean()
            .describe('Whether the fulfillment method is enabled.')
            .optional()
            .nullable(),
          fee: z
            .string()
            .describe('Fee for using this fulfillment method.')
            .optional()
            .nullable(),
          availability: z
            .object({
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .optional(),
              timeZone: z
                .string()
                .describe(
                  'The timezone in which the availability times are given.'
                )
                .optional()
                .nullable(),
            })
            .describe('Availability of this fulfillment method.')
            .optional(),
          minOrderPrice: z
            .string()
            .describe(
              'Minimum order price to qualify for using this fulfillment method.'
            )
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .describe(
              'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags used to classify and sort different types of fulfillment methods.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z.never().optional(),
          }),
          z.object({
            deliveryOptions: z.never().optional(),
            pickupOptions: z
              .object({
                instructions: z
                  .string()
                  .describe('Instructions for the pickup.')
                  .max(250)
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe(
                    'Pickup address.\n\nThis is set to the address of the restaurant.'
                  )
                  .optional(),
              })
              .describe('Data specific for pickup fulfillment method.'),
          }),
          z.object({
            pickupOptions: z.never().optional(),
            deliveryOptions: z
              .object({
                deliveryTimeInMinutes: z
                  .number()
                  .int()
                  .describe('Estimated delivery time in minutes.')
                  .min(0)
                  .optional()
                  .nullable(),
                freeDeliveryThreshold: z
                  .string()
                  .describe(
                    'Threshold for offering free delivery.\nIf the order price exceeds this threshold, the delivery fee is waived.'
                  )
                  .optional()
                  .nullable(),
                deliveryArea: z
                  .intersection(
                    z.object({
                      type: z
                        .enum([
                          'UNKNOWN_DELIVERY_AREA',
                          'RADIUS',
                          'POSTAL_CODE',
                          'CUSTOM',
                          'PROVIDER_DEFINED',
                        ])
                        .describe('Type of delivery area.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                      }),
                      z.object({
                        postalCodeOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        radiusOptions: z
                          .object({
                            minDistance: z
                              .string()
                              .describe(
                                'Minimum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            maxDistance: z
                              .string()
                              .describe(
                                'Maximum distance value.\nThe unit of the radius is specified in the `unit` field.'
                              )
                              .optional()
                              .nullable(),
                            centerPointAddress: z
                              .object({
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine1: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Address at the center of the circle.')
                              .optional(),
                            unit: z
                              .enum(['UNKNOWN_UNIT', 'MILES', 'KILOMETERS'])
                              .describe('Unit of measurement of the radius.')
                              .optional(),
                          })
                          .describe('Settings for a radius delivery area.'),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        customOptions: z.never().optional(),
                        postalCodeOptions: z
                          .object({
                            countryCode: z
                              .string()
                              .describe(
                                'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                              )
                              .optional()
                              .nullable(),
                            postalCodes: z
                              .array(z.string())
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'Settings for a postal code delivery area.'
                          ),
                      }),
                      z.object({
                        radiusOptions: z.never().optional(),
                        postalCodeOptions: z.never().optional(),
                        customOptions: z
                          .object({
                            geocodes: z
                              .array(
                                z.object({
                                  latitude: z
                                    .number()
                                    .describe('Address latitude.')
                                    .optional()
                                    .nullable(),
                                  longitude: z
                                    .number()
                                    .describe('Address longitude.')
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .optional(),
                          })
                          .describe('Settings for a custom delivery area.'),
                      }),
                    ])
                  )
                  .describe(
                    'Delivery area supported by this delivery fulfillment method.'
                  )
                  .optional(),
                deliveryProviderAppId: z
                  .string()
                  .describe('Delivery provider app id.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                courierPickupInstructions: z
                  .string()
                  .describe('Pickup instructions for couriers.')
                  .max(250)
                  .optional()
                  .nullable(),
              })
              .describe('Data specific for delivery fulfillment method.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const GetAccumulatedFulfillmentMethodsAvailabilityRequest = z.object({
  options: z
    .object({ fulfillmentMethodIds: z.array(z.string()).max(500).optional() })
    .optional(),
});
export const GetAccumulatedFulfillmentMethodsAvailabilityResponse = z.object({
  availability: z
    .object({
      availableTimes: z
        .array(
          z.object({
            dayOfWeek: z
              .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
              .describe('The day of week this availability relates to.')
              .optional(),
            timeRanges: z
              .array(
                z.object({
                  startTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The start time in time of day representation.')
                    .optional(),
                  endTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The end time in time of day representation.')
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .optional(),
      timeZone: z
        .string()
        .describe('The timezone in which the availability times are given.')
        .optional()
        .nullable(),
    })
    .describe('The accumulated availability of all fulfillment methods.')
    .optional(),
  types: z
    .array(z.enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY']))
    .optional(),
});
export const GetCombinedMethodAvailabilityRequest = z.object({
  fulfillmentMethodIds: z.array(z.string()).max(500),
});
export const GetCombinedMethodAvailabilityResponse = z.object({
  combinedAvailability: z
    .object({
      availableTimes: z
        .array(
          z.object({
            dayOfWeek: z
              .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
              .describe('The day of week this availability relates to.')
              .optional(),
            timeRanges: z
              .array(
                z.object({
                  startTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The start time in time of day representation.')
                    .optional(),
                  endTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The end time in time of day representation.')
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .optional(),
      timeZone: z
        .string()
        .describe('The timezone in which the availability times are given.')
        .optional()
        .nullable(),
    })
    .describe('The combined availability of the given fulfillment methods.')
    .optional(),
  fulfillmentTypes: z
    .array(z.enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY']))
    .max(100)
    .optional(),
});
export const GetAggregatedMethodAvailabilityRequest = z.object({
  fulfillmentMethodIds: z.array(z.string()).max(500),
});
export const GetAggregatedMethodAvailabilityResponse = z.object({
  aggregatedAvailability: z
    .object({
      availableTimes: z
        .array(
          z.object({
            dayOfWeek: z
              .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
              .describe('The day of week this availability relates to.')
              .optional(),
            timeRanges: z
              .array(
                z.object({
                  startTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The start time in time of day representation.')
                    .optional(),
                  endTime: z
                    .object({
                      hours: z
                        .number()
                        .int()
                        .describe('Hours. <br />\nMin: `0`. <br />\nMax: `23`.')
                        .optional(),
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                        )
                        .optional(),
                    })
                    .describe('The end time in time of day representation.')
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .optional(),
      timeZone: z
        .string()
        .describe('The timezone in which the availability times are given.')
        .optional()
        .nullable(),
    })
    .describe('The aggregated availability of the given fulfillment methods.')
    .optional(),
  fulfillmentTypes: z
    .array(z.enum(['UNKNOWN_FULFILLMENT_TYPE', 'PICKUP', 'DELIVERY']))
    .max(100)
    .optional(),
});
export const BulkUpdateFulfillmentMethodTagsRequest = z.object({
  fulfillmentMethodIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the fulfillment methods.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the fulfillment methods.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateFulfillmentMethodTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated fulfillment method.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateFulfillmentMethodTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter that determines which fulfillment methods to update tags for.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the fulfillment methods.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the fulfillment methods.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateFulfillmentMethodTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
